import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Typography,
  Statistic,
  Table,
  Tag,
  Spin,
  Empty,
  List,
  Space,
} from "antd";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { ColumnsType } from "antd/es/table";
import {
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  WarningOutlined,
  SearchOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { api } from "@/services/api";
import { API_ENDPOINTS } from "@/constants";
import { formatRelative, formatShortDate } from "@/utils/date";
import type { ApiResponse, Deck, DeckAnalytics, CardAnalytics } from "@/types";

const { Title, Text, Paragraph } = Typography;

interface DeckStudentAnalytics {
  student: { id: string; name: string; avatarId?: string | null };
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED";
  assignedAt: string;
  lastSession: {
    studentId: string;
    completedAt: string;
    cardsUnderstood: number;
    cardsReviewed: number;
    durationSeconds: number;
  } | null;
}

interface DeckComment {
  id: string;
  student: { id: string; name: string; avatarId?: string | null };
  comment: string;
  completedAt: string;
}

const PIE_COLORS = ["#16a34a", "#94a3b8"];

export default function AnalyticsPage() {
  const [selectedDeckId, setSelectedDeckId] = useState<string | undefined>(
    undefined,
  );
  const [studentSearch, setStudentSearch] = useState("");
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Fetch published decks for the selector
  const { data: decksResponse, isLoading: loadingDecks } = useQuery({
    queryKey: ["teacher", "decks", "published"],
    queryFn: () =>
      api.get<never, any>(API_ENDPOINTS.DECKS, {
        params: { status: "PUBLISHED" },
      }),
  });

  const _decksData = decksResponse?.data;
  const publishedDecks = (
    Array.isArray(_decksData)
      ? _decksData
      : Array.isArray(_decksData?.data)
        ? _decksData.data
        : []
  ) as Deck[];

  useEffect(() => {
    if (publishedDecks.length > 0 && !selectedDeckId) {
      setSelectedDeckId(publishedDecks[0].id);
    }
  }, [publishedDecks, selectedDeckId]);

  // Fetch deck overview analytics
  const { data: overviewResponse, isLoading: loadingOverview } = useQuery({
    queryKey: ["analytics", "deck", selectedDeckId, "overview"],
    queryFn: () =>
      api.get<never, ApiResponse<DeckAnalytics>>(
        `${API_ENDPOINTS.ANALYTICS_DECKS}/${selectedDeckId}`,
      ),
    enabled: Boolean(selectedDeckId),
  });

  // Fetch per-card analytics
  const { data: cardsResponse, isLoading: loadingCards } = useQuery({
    queryKey: ["analytics", "deck", selectedDeckId, "cards"],
    queryFn: () =>
      api.get<never, ApiResponse<CardAnalytics[]>>(
        `${API_ENDPOINTS.ANALYTICS_DECKS}/${selectedDeckId}/cards`,
      ),
    enabled: Boolean(selectedDeckId),
  });

  // Fetch per-student analytics
  const { data: studentsResponse, isLoading: loadingStudents } = useQuery({
    queryKey: ["analytics", "deck", selectedDeckId, "students"],
    queryFn: () =>
      api.get<never, ApiResponse<DeckStudentAnalytics[]>>(
        `${API_ENDPOINTS.ANALYTICS_DECKS}/${selectedDeckId}/students`,
      ),
    enabled: Boolean(selectedDeckId),
  });

  // Fetch comments
  const { data: commentsResponse, isLoading: loadingComments } = useQuery({
    queryKey: ["analytics", "deck", selectedDeckId, "comments"],
    queryFn: () =>
      api.get<never, ApiResponse<DeckComment[]>>(
        `${API_ENDPOINTS.ANALYTICS_DECKS}/${selectedDeckId}/comments`,
      ),
    enabled: Boolean(selectedDeckId),
  });

  const overview = overviewResponse?.data;
  const cardAnalytics = cardsResponse?.data ?? [];
  const studentAnalytics = studentsResponse?.data ?? [];
  const comments = commentsResponse?.data ?? [];

  const filteredStudentAnalytics = useMemo(() => {
    if (!studentSearch.trim()) return studentAnalytics;
    const lower = studentSearch.toLowerCase();
    return studentAnalytics.filter((s) =>
      s.student?.name?.toLowerCase().includes(lower),
    );
  }, [studentAnalytics, studentSearch]);

  // Chart data
  const barChartData = cardAnalytics.map((card, index) => ({
    name: card.title ?? `Card ${index + 1}`,
    understood: Math.round((card.understoodRate ?? 0) * 100),
  }));

  const completedCount = overview?.completedStudents ?? 0;
  const totalAssigned = overview?.totalStudents ?? 0;
  const notCompletedCount = totalAssigned - completedCount;
  const pieData = [
    { name: "Completed", value: completedCount },
    {
      name: "Not Completed",
      value: notCompletedCount > 0 ? notCompletedCount : 0,
    },
  ];

  const cardColumns: ColumnsType<CardAnalytics> = [
    {
      title: "#",
      key: "index",
      width: 48,
      render: (_, record) => record.sequenceOrder || 1,
    },
    {
      title: "Card",
      key: "card",
      ellipsis: true,
      render: (_, record) => <Text strong>{record.title}</Text>,
    },
    {
      title: "Times Reviewed",
      key: "reviewed",
      width: 130,
      render: (_, record) => record.understood + record.notUnderstood,
      sorter: (a, b) =>
        a.understood + a.notUnderstood - (b.understood + b.notUnderstood),
    },
    {
      title: "Understood %",
      key: "understoodRate",
      width: 140,
      sorter: (a, b) => (a.understoodRate ?? 0) - (b.understoodRate ?? 0),
      render: (_, record) => {
        const pct = Math.round(record.understoodRate ?? 0);
        const isStruggling = pct < 50;
        return (
          <Space>
            <Text
              strong
              style={{ color: isStruggling ? "#ef4444" : "#16a34a" }}
            >
              {pct}%
            </Text>
            {isStruggling && (
              <WarningOutlined style={{ color: "#ef4444", fontSize: 14 }} />
            )}
          </Space>
        );
      },
    },
  ];

  const studentColumns: ColumnsType<DeckStudentAnalytics> = [
    {
      title: "Student",
      key: "name",
      render: (_, record) => record.student?.name ?? "—",
      sorter: (a, b) =>
        (a.student?.name ?? "").localeCompare(b.student?.name ?? ""),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          COMPLETED: "green",
          IN_PROGRESS: "blue",
          PENDING: "gold",
        };
        return (
          <Tag color={colorMap[status] ?? "default"}>
            {status?.replace("_", " ") ?? "—"}
          </Tag>
        );
      },
      filters: [
        { text: "Completed", value: "COMPLETED" },
        { text: "In Progress", value: "IN_PROGRESS" },
        { text: "Pending", value: "PENDING" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Understood Rate",
      key: "understoodRate",
      render: (_, record) => {
        const { cardsUnderstood = 0, cardsReviewed = 0 } =
          record.lastSession ?? {};
        const pct =
          cardsReviewed > 0
            ? Math.round((cardsUnderstood / cardsReviewed) * 100)
            : 0;
        return (
          <Text
            style={{ color: pct < 50 ? "#ef4444" : "#16a34a", fontWeight: 600 }}
          >
            {record.lastSession ? `${pct}%` : "—"}
          </Text>
        );
      },
      sorter: (a, b) => {
        const rateA = a.lastSession
          ? a.lastSession.cardsUnderstood / (a.lastSession.cardsReviewed || 1)
          : 0;
        const rateB = b.lastSession
          ? b.lastSession.cardsUnderstood / (b.lastSession.cardsReviewed || 1)
          : 0;
        return rateA - rateB;
      },
    },
    {
      title: "Session Date",
      key: "sessionDate",
      render: (_, record) => (
        <Text type="secondary">
          {record.lastSession?.completedAt
            ? formatShortDate(record.lastSession.completedAt)
            : "-"}
        </Text>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 20 }}>
        Analytics
      </Title>

      <Card style={{ borderRadius: 12, marginBottom: 20 }}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Select a deck to view analytics
        </Text>
        <Select
          placeholder="Choose a published deck..."
          style={{ width: "100%", maxWidth: 480 }}
          value={selectedDeckId}
          onChange={setSelectedDeckId}
          loading={loadingDecks}
          showSearch
          optionFilterProp="label"
          allowClear
          onClear={() => setSelectedDeckId(undefined)}
          options={publishedDecks.map((d) => ({
            value: d.id,
            label: `${d.topicName} (${d.cardCount} cards)`,
          }))}
        />
      </Card>

      {!selectedDeckId ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select a deck above to view its analytics"
        />
      ) : (
        <>
          {/* Overview Stats */}
          {loadingOverview ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin />
            </div>
          ) : overview ? (
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col xs={24} sm={8}>
                <Card hoverable style={{ borderRadius: 12 }}>
                  <Statistic
                    title="Completion Rate"
                    value={Math.round(overview.completionRate * 100)}
                    suffix="%"
                    prefix={
                      <CheckCircleOutlined style={{ color: "#16a34a" }} />
                    }
                    valueStyle={{ color: "#16a34a" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card hoverable style={{ borderRadius: 12 }}>
                  <Statistic
                    title="Average Score"
                    value={Math.round(overview.averageScore * 100)}
                    suffix="%"
                    // prefix={<PercentageOutlined style={{ color: '#6366f1' }} />}
                    valueStyle={{ color: "#6366f1" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card hoverable style={{ borderRadius: 12 }}>
                  <Statistic
                    title="Avg. Duration"
                    value={Math.round(overview.averageDurationSeconds / 60)}
                    suffix="min"
                    prefix={
                      <ClockCircleOutlined style={{ color: "#0891b2" }} />
                    }
                    valueStyle={{ color: "#0891b2" }}
                  />
                </Card>
              </Col>
              {overview.averageComfort != null && (
                <Col xs={24} sm={8}>
                  <Card hoverable style={{ borderRadius: 12 }}>
                    <Statistic
                      title="Avg Comfort"
                      value={overview.averageComfort.toFixed(1)}
                      suffix="/ 5"
                      valueStyle={{ color: "#6366f1" }}
                    />
                  </Card>
                </Col>
              )}
            </Row>
          ) : null}

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={24} lg={14}>
              <Card
                title={
                  <Space>
                    <BarChartOutlined />
                    <span>Per-Card Understanding Rate</span>
                  </Space>
                }
                style={{ borderRadius: 12 }}
                loading={loadingCards}
              >
                {barChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart
                      data={barChartData}
                      margin={{ top: 8, right: 16, left: 0, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11 }}
                        interval={0}
                        angle={-35}
                        textAnchor="end"
                        height={100}
                        tickFormatter={(v: string) =>
                          v.length > 18 ? `${v.slice(0, 17)}…` : v
                        }
                      />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fontSize: 12 }}
                        unit="%"
                      />
                      <RechartsTooltip
                        formatter={(value: number) => [
                          `${value}%`,
                          "Understood",
                        ]}
                      />
                      <Bar
                        dataKey="understood"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={48}
                      >
                        {barChartData.map((entry, index) => (
                          <Cell
                            key={index}
                            fill={entry.understood < 50 ? "#ef4444" : "#6366f1"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="No card data yet" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={10}>
              <Card
                title={
                  <Space>
                    <CheckCircleOutlined />
                    <span>Completion Overview</span>
                  </Space>
                }
                style={{ borderRadius: 12, minHeight: 437 }}
                loading={loadingOverview}
              >
                {totalAssigned > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={65}
                        outerRadius={95}
                        paddingAngle={4}
                        dataKey="value"
                      >
                        {pieData.map((_, index) => (
                          <Cell key={index} fill={PIE_COLORS[index]} />
                        ))}
                      </Pie>
                      <Legend />
                      <RechartsTooltip
                        formatter={(value: number, name: string) => [
                          value,
                          name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="No assignment data yet" />
                )}
              </Card>
            </Col>
          </Row>

          {/* Comfort Distribution Chart */}
          {overview?.comfortDistribution && (
            <Card
              title={
                <Space>
                  <BarChartOutlined />
                  <span>Comfort Distribution</span>
                </Space>
              }
              style={{ borderRadius: 12, marginBottom: 20 }}
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={[1, 2, 3, 4, 5].map((level) => ({
                    level: String(level),
                    count: overview.comfortDistribution?.[level] ?? 0,
                  }))}
                  margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="level" tick={{ fontSize: 13 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <RechartsTooltip
                    formatter={(value: number) => [value, "Interactions"]}
                  />
                  <Bar
                    dataKey="count"
                    fill="#6366f1"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={56}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Per-Card Table */}
          <Card
            title="Per-Card Breakdown"
            style={{ borderRadius: 12, marginBottom: 20 }}
            loading={loadingCards}
          >
            {!isMobile ? (
              <Table
                columns={cardColumns}
                dataSource={cardAnalytics}
                rowKey="cardId"
                pagination={false}
                locale={{ emptyText: "No card interaction data yet" }}
              />
            ) : cardAnalytics.length > 0 ? (
              <List
                dataSource={cardAnalytics}
                renderItem={(card, index) => {
                  const pct = Math.round(card.understoodRate ?? 0);
                  const isStruggling = pct < 50;
                  return (
                    <List.Item>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          width: "100%",
                          gap: 12,
                        }}
                      >
                        <Space align="start">
                          <Text type="secondary" style={{ minWidth: 20 }}>
                            {card.sequenceOrder || index + 1}.
                          </Text>
                          <div>
                            <Text strong style={{ display: "block" }}>
                              {card.title}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Reviewed {card.understood + card.notUnderstood}{" "}
                              times
                            </Text>
                          </div>
                        </Space>
                        <Space size={4} style={{ flexShrink: 0 }}>
                          <Text
                            strong
                            style={{
                              color: isStruggling ? "#ef4444" : "#16a34a",
                            }}
                          >
                            {pct}%
                          </Text>
                          {isStruggling && (
                            <WarningOutlined
                              style={{ color: "#ef4444", fontSize: 14 }}
                            />
                          )}
                        </Space>
                      </div>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty description="No card interaction data yet" />
            )}
          </Card>

          {/* Per-Student Table */}
          <Card
            title="Per-Student Results"
            style={{ borderRadius: 12, marginBottom: 20 }}
            loading={loadingStudents}
          >
            <Input
              placeholder="Search by student name..."
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              suffix={
                studentSearch ? (
                  <CloseCircleFilled
                    style={{
                      fontSize: 16,
                      color: "#8c8c8c",
                      cursor: "pointer",
                    }}
                    onClick={() => setStudentSearch("")}
                  />
                ) : null
              }
              style={{ maxWidth: isMobile ? "100%" : 360, marginBottom: 16 }}
            />
            {!isMobile ? (
              <Table
                columns={studentColumns}
                dataSource={filteredStudentAnalytics}
                rowKey={(r) => r.student?.id ?? Math.random().toString()}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                locale={{
                  emptyText: studentSearch.trim()
                    ? `No students found matching "${studentSearch}"`
                    : "No student data yet",
                }}
              />
            ) : filteredStudentAnalytics.length > 0 ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {filteredStudentAnalytics.map((record) => {
                  const { cardsUnderstood = 0, cardsReviewed = 0 } =
                    record.lastSession ?? {};
                  const pct =
                    cardsReviewed > 0
                      ? Math.round((cardsUnderstood / cardsReviewed) * 100)
                      : 0;
                  const colorMap: Record<string, string> = {
                    COMPLETED: "green",
                    IN_PROGRESS: "blue",
                    PENDING: "gold",
                  };
                  return (
                    <div
                      key={record.student?.id}
                      style={{
                        padding: "12px 14px",
                        borderRadius: 10,
                        border: "1px solid #f0f0f0",
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <Text strong style={{ fontSize: 14 }}>
                          {record.student?.name ?? "—"}
                        </Text>
                        <Tag
                          color={colorMap[record.status] ?? "default"}
                          style={{ margin: 0, flexShrink: 0 }}
                        >
                          {record.status?.replace("_", " ") ?? "—"}
                        </Tag>
                      </div>
                      <div style={{ display: "flex", gap: 20 }}>
                        <div>
                          <Text
                            type="secondary"
                            style={{ fontSize: 11, display: "block" }}
                          >
                            Understood Rate
                          </Text>
                          <Text
                            style={{
                              fontSize: 13,
                              color: pct < 50 ? "#ef4444" : "#16a34a",
                              fontWeight: 600,
                            }}
                          >
                            {record.lastSession ? `${pct}%` : "—"}
                          </Text>
                        </div>
                        <div>
                          <Text
                            type="secondary"
                            style={{ fontSize: 11, display: "block" }}
                          >
                            Session Date
                          </Text>
                          <Text style={{ fontSize: 13 }}>
                            {record.lastSession?.completedAt
                              ? formatShortDate(record.lastSession.completedAt)
                              : "—"}
                          </Text>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty
                description={
                  studentSearch.trim()
                    ? `No students found matching "${studentSearch}"`
                    : "No student data yet"
                }
              />
            )}
          </Card>

          {/* Comments */}
          <Card
            title={
              <Space>
                <MessageOutlined />
                <span>Student Feedback</span>
              </Space>
            }
            style={{ borderRadius: 12, marginBottom: 20 }}
            loading={loadingComments}
          >
            {comments.length > 0 ? (
              <List
                dataSource={comments}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{item.student?.name ?? "—"}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {formatRelative(item.completedAt)}
                          </Text>
                        </Space>
                      }
                      description={
                        <Paragraph style={{ margin: 0 }}>
                          {item.comment}
                        </Paragraph>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No feedback yet" />
            )}
          </Card>
        </>
      )}
    </div>
  );
}
